import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import FocusedFlowPage from "../../../components/FocusedFlowPage";
import { fetchPatientByAccountNumber } from "../../../utils/api";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const VerifyPatientAccount = () => {
  const [ehrNumber, setEhrNumber] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [validationMessage, setValidationMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [patientExists, setPatientExists] = useState(null);
  const [foundPatient, setFoundPatient] = useState(null);
  const [buttonText, setButtonText] = useState("VERIFY ACCOUNT");
  const validationTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const flow = location.state?.flow || "schedule";
  const returnTo = location.state?.returnTo || BIOBANK_ROUTES.PARTICIPANT_SCHEDULING;

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  const verifyPatientExists = async (accountNumber) => {
    try {
      setIsVerifying(true);
      const patient = await fetchPatientByAccountNumber(accountNumber);
      setPatientExists(patient !== null);
      setFoundPatient(patient);
      return patient;
    } catch (error) {
      console.error("Error verifying patient:", error);
      setPatientExists(false);
      setFoundPatient(null);
      return null;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleEhrChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    const numericValue = value.replace(/\D/g, "");
    setEhrNumber(numericValue);

    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Reset verification state
    setPatientExists(null);
    setIsVerifying(false);
    setFoundPatient(null);

    // Real-time validation feedback
    if (numericValue.length === 0) {
      setValidationMessage("");
      setDisabled(true);
      setButtonText("VERIFY ACCOUNT");
    } else if (numericValue.length < 7) {
      setValidationMessage(
        `Enter ${7 - numericValue.length} more digit${
          7 - numericValue.length === 1 ? "" : "s"
        }`
      );
      setDisabled(true);
      setButtonText("VERIFY ACCOUNT");
    } else if (numericValue.length === 7) {
      setValidationMessage("✓ Valid MRN ID format - Verifying...");
      setDisabled(true);
      setButtonText("VERIFYING...");

      // Debounce the API call
      validationTimeoutRef.current = setTimeout(async () => {
        const patient = await verifyPatientExists(numericValue);
        if (patient) {
          const patientName = `${patient.firstName || ""} ${
            patient.lastName || ""
          }`.trim();
          setValidationMessage(
            `✓ Participant found: ${patientName} - Ready to continue`
          );
          setDisabled(false);
          setButtonText("CONTINUE");
        } else {
          setValidationMessage("⚠ No participant found with this MRN ID. Please register them first.");
          setDisabled(true); // Disable button because we can't verify
          setButtonText("VERIFY ACCOUNT");
        }
      }, 500);
    } else {
      setValidationMessage("MRN ID must be exactly 7 digits");
      setDisabled(true);
      setButtonText("VERIFY ACCOUNT");
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const trimmedEhr = ehrNumber.trim();

    if (!/^\d{7}$/.test(trimmedEhr)) return;

    try {
      // Use the patient data we already fetched during real-time verification
      if (foundPatient) {
        console.log("✅ Existing participant found:", foundPatient);
        
        // Both Drop-In and Schedule follow the same routing now
        navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_SCHEDULING_CONFIRM, {
          state: {
            ...foundPatient,
            matchedEhr: trimmedEhr,
            existingPatient: true,
            flow,
            returnTo,
            isDropIn: flow === "dropIn"
          },
        });
      }
      // If not found, they can't even click the button, so no else block needed.
    } catch (err) {
      console.error("Verification API call failed:", err);
    }
  };

  return (
    <FocusedFlowPage
      title="Verify Participant Account"
      subtitle="Before proceeding, we need to verify if the participant already has an account. Please enter their 7-digit Medical Record Number (MRN) below."
      onClose={() => navigate(returnTo)}
      headerActions={
        <Button 
          variant="contained" 
          onClick={() => navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_SCHEDULING_ADD, { state: { flow, returnTo, isDropIn: flow === "dropIn" } })} 
          sx={{ fontWeight: 600, height: "36px", px: 2, fontSize: "13px", whiteSpace: "nowrap", backgroundColor: "var(--tamu-maroon)", borderRadius: "4px" }}
        >
          REGISTER
        </Button>
      }
      maxWidth={640}
      actions={[
        <Button
          key="back"
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(returnTo)}
        >
          BACK
        </Button>,
        <Button
          key="submit"
          type="submit"
          form="verify-patient-form"
          variant="contained"
          disabled={disabled}
          sx={{
            backgroundColor: "var(--tamu-maroon)",
            borderRadius: 0,
            fontWeight: 600,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "#3c0000",
            },
          }}
        >
          {buttonText}
        </Button>,
      ]}
    >
      <form id="verify-patient-form" onSubmit={handleVerify}>
        <Box sx={{ marginX: "auto", width: "90%" }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "15px",
              color: "#222",
              mb: "-5px",
            }}
          >
            Participant MRN
            <span style={{ color: "red", marginLeft: "3px" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter 7 digits MRN"
            value={ehrNumber}
            onChange={handleEhrChange}
            slotProps={{
              htmlInput: {
                maxLength: 7,
                pattern: "[0-9]*",
                inputMode: "numeric",
              },
            }}
            sx={{
              mt: 1,
              input: {
                padding: "10px",
                fontSize: "15px",
                backgroundColor: "white",
                borderRadius: "6px",
              },
            }}
          />
          {validationMessage && (
            <Typography
              sx={{
                fontSize: "13px",
                color: validationMessage.includes("✓")
                  ? "#4caf50"
                  : validationMessage.includes("⚠")
                  ? "#ff9800"
                  : "#666",
                mt: 1,
                ml: 1,
                fontWeight:
                  validationMessage.includes("✓") ||
                  validationMessage.includes("⚠")
                    ? 500
                    : 400,
              }}
            >
              {validationMessage}
            </Typography>
          )}
        </Box>
      </form>
    </FocusedFlowPage>
  );
};

export default VerifyPatientAccount;
